import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createCalendarEvent, checkTaskConflict } from '../../services/calendar';
import { getAllUsers } from '../../services/users';
import { getTaskTypes } from '../../services/taskTypes';
import { getMainContact } from '../../services/contacts';
import { getClientSiteAddresses } from '../../services/siteAddresses';
import { createAuditLog } from '../../services/audit';
import './ScheduleTaskTab.css';

export default function ScheduleTaskTab({ clientId, client }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [mainContact, setMainContact] = useState(null);
  const [siteAddresses, setSiteAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '10:00',
    all_day: false,
    assigned_to: '',
    task_type: 'general',
    site_address_id: '',
    contact_name: '',
    contact_phone: '',
    require_arrival_time: null,
    require_start_time: null,
  });
  const [taskTypes, setTaskTypes] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [usersData, contactData, addressesData, taskTypesData] = await Promise.all([
        getAllUsers(),
        getMainContact(clientId),
        getClientSiteAddresses(clientId),
        getTaskTypes(true),
      ]);
      
      setUsers(usersData);
      setMainContact(contactData);
      setSiteAddresses(addressesData);
      setTaskTypes(taskTypesData || []);
      
      // Pre-fill contact info if main contact exists
      if (contactData) {
        setFormData(prev => ({
          ...prev,
          contact_name: contactData.name || '',
          contact_phone: contactData.phone || '',
        }));
      }
      
      // Pre-select primary address if it exists
      if (addressesData && addressesData.length > 0) {
        const primary = addressesData.find(addr => addr.is_primary) || addressesData[0];
        if (primary) {
          setFormData(prev => ({
            ...prev,
            site_address_id: primary.id.toString(),
          }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const startDateTime = formData.all_day
        ? new Date(formData.start_date + 'T00:00:00')
        : new Date(formData.start_date + 'T' + formData.start_time);
      
      const endDateTime = formData.all_day
        ? (formData.end_date ? new Date(formData.end_date + 'T23:59:59') : null)
        : (formData.end_date && formData.end_time ? new Date(formData.end_date + 'T' + formData.end_time) : null);

      // Build description with contact and address info
      let description = formData.description || '';
      
      // Add contact info
      if (formData.contact_name || formData.contact_phone) {
        const contactInfo = [];
        if (formData.contact_name) contactInfo.push(`Contact: ${formData.contact_name}`);
        if (formData.contact_phone) contactInfo.push(`Phone: ${formData.contact_phone}`);
        if (contactInfo.length > 0) {
          description = (description ? description + '\n\n' : '') + contactInfo.join('\n');
        }
      }
      
      // Add site address info
      if (formData.site_address_id) {
        const selectedAddress = siteAddresses.find(addr => addr.id.toString() === formData.site_address_id);
        if (selectedAddress) {
          const addressParts = [
            selectedAddress.address_line1,
            selectedAddress.address_line2,
            `${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zip_code}`,
          ].filter(Boolean);
          if (addressParts.length > 0) {
            description = (description ? description + '\n\n' : '') + 'Site Address:\n' + addressParts.join('\n');
          }
        }
      }

      // If no end date/time provided, default to 1 hour after start
      const finalEndDateTime = endDateTime || (formData.all_day ? null : new Date(startDateTime.getTime() + 60 * 60 * 1000));

      // Check for task conflicts if a user is assigned
      if (formData.assigned_to) {
        const conflicts = await checkTaskConflict(
          formData.assigned_to,
          startDateTime,
          finalEndDateTime,
          formData.all_day,
          null // No exclude ID for new events
        );

        if (conflicts.length > 0) {
          const conflictTitles = conflicts.map(c => c.title).join(', ');
          const conflictUser = users.find(u => u.id === formData.assigned_to);
          const userName = conflictUser?.full_name || conflictUser?.email || 'the selected user';
          setError(`${userName} already has ${conflicts.length} task(s) scheduled at this time: ${conflictTitles}. Please choose a different time or assign to a different user.`);
          setLoading(false);
          return;
        }
      }

      // Validate required fields
      if (!formData.assigned_to) {
        setError('Assignee is required. Please select a user to assign this task to.');
        setLoading(false);
        return;
      }

      const eventData = {
        title: formData.title,
        description: description,
        start_date: startDateTime.toISOString(),
        end_date: finalEndDateTime ? finalEndDateTime.toISOString() : null,
        all_day: formData.all_day,
        client_id: clientId,
        assigned_to: formData.assigned_to, // Required - no null fallback
        task_type: formData.task_type || 'general',
        require_arrival_time: formData.require_arrival_time ?? null,
        require_start_time: formData.require_start_time ?? null,
        created_by: user?.id,
      };

      const newEvent = await createCalendarEvent(eventData);
      await createAuditLog('create', 'calendar_event', newEvent.id, {
        title: eventData.title,
        client_id: clientId,
        client_name: client?.name,
        assigned_to: formData.assigned_to,
      });

      setSuccess('Task scheduled successfully!');
      
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Reset form but keep contact and address pre-filled
      const primaryAddress = siteAddresses.find(addr => addr.is_primary) || (siteAddresses.length > 0 ? siteAddresses[0] : null);
      setFormData({
        title: '',
        description: '',
        start_date: '',
        start_time: '09:00',
        end_date: '',
        end_time: '10:00',
        all_day: false,
        assigned_to: '',
        task_type: 'general',
        site_address_id: primaryAddress ? primaryAddress.id.toString() : '',
        contact_name: mainContact?.name || '',
        contact_phone: mainContact?.phone || '',
        require_arrival_time: null,
        require_start_time: null,
      });

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message || 'Failed to schedule task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-task-tab">
      <div className="tab-header">
        <h2>Schedule Task</h2>
        <p className="tab-description">Create a task for this client that will appear on the calendar.</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="task-form">
        <div className="form-group">
          <label>Task Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Follow up call, Site visit, Installation"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            rows="4"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Task details and notes..."
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label checkbox-row">
            <input
              type="checkbox"
              checked={formData.all_day}
              onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
            />
            <span>All day event</span>
          </label>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="date"
              required
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          {!formData.all_day && (
            <div className="form-group">
              <label>Start Time *</label>
              <input
                type="time"
                required
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            />
          </div>
          {!formData.all_day && (
            <div className="form-group">
              <label>End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Main Contact</label>
          <div className="contact-info-display">
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="Contact name"
            />
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              placeholder="Contact phone"
            />
          </div>
          <small className="form-hint">
            {mainContact 
              ? `Pre-filled from main contact: ${mainContact.name}. You can edit if needed.`
              : 'No main contact set. Enter contact information manually.'}
          </small>
        </div>

        <div className="form-group">
          <label>Site Address</label>
          {siteAddresses.length === 0 ? (
            <div className="no-address-message">
              <p>No site addresses added for this client. Add addresses in the Contact Info tab.</p>
            </div>
          ) : siteAddresses.length === 1 ? (
            <div className="address-display">
              <pre>{[
                siteAddresses[0].address_line1,
                siteAddresses[0].address_line2,
                `${siteAddresses[0].city}, ${siteAddresses[0].state} ${siteAddresses[0].zip_code}`,
              ].filter(Boolean).join('\n')}</pre>
            </div>
          ) : (
            <select
              value={formData.site_address_id}
              onChange={(e) => setFormData({ ...formData, site_address_id: e.target.value })}
            >
              <option value="">Select a site address</option>
              {siteAddresses.map(address => (
                <option key={address.id} value={address.id}>
                  {address.address_name || 'Unnamed Address'} - {address.address_line1}, {address.city}
                  {address.is_primary ? ' (Primary)' : ''}
                </option>
              ))}
            </select>
          )}
          <small className="form-hint">
            {siteAddresses.length > 1 
              ? 'Select the site address for this task. The address will be included in the task description.'
              : siteAddresses.length === 1
              ? 'This address will be included in the task description.'
              : 'Add site addresses in the Contact Info tab to have them automatically included in tasks.'}
          </small>
        </div>

        <div className="form-group">
          <label>Task Type *</label>
          <select
            value={formData.task_type}
            onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
            required
          >
            {taskTypes.length > 0 ? (
              taskTypes.map(tt => (
                <option key={tt.id} value={tt.code}>{tt.name}</option>
              ))
            ) : (
              <>
                <option value="general">General</option>
                <option value="call">Call</option>
                <option value="follow_up">Follow-Up</option>
                <option value="estimate_follow_up">Estimate Follow-Up</option>
                <option value="invoice_follow_up">Invoice Follow-Up</option>
                <option value="site_visit">Site Visit</option>
              </>
            )}
          </select>
          <small className="form-hint">Select the type of task. This affects completion behavior and calendar colors.</small>
        </div>

        <div className="form-group">
          <h4 className="form-subheading">Arrival &amp; start time (this task)</h4>
          <p className="form-hint" style={{ marginBottom: '0.5rem' }}>
            When unchecked, the task type default from Settings is used.
          </p>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.require_arrival_time === true}
              onChange={(e) => setFormData(prev => ({ ...prev, require_arrival_time: e.target.checked ? true : null }))}
            />
            Require arrival time (user must enter when they arrived)
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.require_start_time === true}
              onChange={(e) => setFormData(prev => ({ ...prev, require_start_time: e.target.checked ? true : null }))}
            />
            Require start time (user must enter when they started)
          </label>
        </div>

        <div className="form-group">
          <label>Assign To *</label>
          <select
            value={formData.assigned_to}
            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
            required
          >
            <option value="">Select a user</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.email} {user.user_metadata?.full_name ? `(${user.user_metadata.full_name})` : ''}
              </option>
            ))}
          </select>
          <small className="form-hint">Select a user to assign this task to. Assignee is required.</small>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading || !formData.title || !formData.assigned_to || !formData.start_date}
          >
            {loading ? 'Scheduling...' : 'Schedule Task'}
          </button>
        </div>
      </form>
    </div>
  );
}
